import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Compass, Shield, Sparkles, PenLine, Network } from "lucide-react";
import { Section, Eyebrow } from "@/components/Section";

const HERO_IMG =
  "https://images.unsplash.com/photo-1524429656589-6633a470097c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const SPECIALTIES = [
  {
    icon: Compass,
    label: "EMDR",
    title: "Reprocessing what your body still remembers.",
    body:
      "Bilateral stimulation that gently moves trauma from frozen to integrated. Evidence-based, paced for your nervous system.",
  },
  {
    icon: Heart,
    label: "TF-CBT",
    title: "Trauma-Focused CBT for younger lives.",
    body:
      "Structured care for adolescents and young adults processing acute or developmental trauma — with caregiver collaboration when helpful.",
  },
  {
    icon: Sparkles,
    label: "CBT & DBT",
    title: "Practical skills for anxious & overwhelmed minds.",
    body:
      "Cognitive Behavioral Therapy for anxiety, depression, and intrusive thinking. DBT skills for emotional regulation and distress tolerance.",
  },
  {
    icon: PenLine,
    label: "WET",
    title: "Written Exposure Therapy — a brief, evidence-based path.",
    body:
      "A structured five-session protocol for PTSD that uses guided writing. Lower demands than longer exposure therapies, with comparable outcomes for many clients.",
  },
  {
    icon: Network,
    label: "MST",
    title: "Multi-Systemic Therapy — care where life happens.",
    body:
      "A family-and-community-centered approach for adolescents whose challenges show up across home, school, peers, and systems — including juvenile justice or child welfare involvement.",
  },
  {
    icon: Shield,
    label: "Affirming Care",
    title: "A safe room for veterans & LGBTQ+ clients.",
    body:
      "No translating, no defending. Identity and lived experience are met with respect, not curiosity.",
  },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Colorado mountains at golden hour"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(249,247,244,0.55) 0%, rgba(249,247,244,0.78) 60%, #F9F7F4 100%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-24 pb-20 sm:pt-36 sm:pb-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 fade-up">
            <Eyebrow>Licensed Clinical Social Worker · Denver, Colorado</Eyebrow>
            <h1 className="font-serif-display mt-6 text-4xl sm:text-6xl lg:text-7xl text-[#2E3630] leading-[1.02] tracking-tight">
              Find grounding in a hectic world.
            </h1>
            <p className="font-body mt-8 text-lg sm:text-xl text-[#5F6B62] max-w-2xl leading-relaxed">
              Therapy for trauma, anxiety, depression, and grief — paced for
              your nervous system. Individual counseling for ages 10 to 50,
              with affirming care for veterans, immigrants, and the LGBTQ+
              community.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-2">
              <Link
                to="/contact"
                data-testid="hero-book-cta"
                className="btn-pill btn-primary"
              >
                Request a consultation <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-cta"
                className="btn-pill btn-outline"
              >
                Meet Jeremy
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 fade-up fade-up-delay-3">
            <div className="bg-white/75 backdrop-blur-xl border border-[#E8E5DF] rounded-[2rem] p-8 shadow-sm">
              <Eyebrow>Currently accepting</Eyebrow>
              <ul className="mt-5 space-y-3 text-[#2E3630] font-body">
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#6B8E7B]" />New individual clients (ages 10–50)</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#6B8E7B]" />Telehealth across Colorado</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#6B8E7B]" />In-person sessions in Denver</li>
              </ul>
              <div className="mt-6 pt-6 border-t border-[#E8E5DF] text-sm text-[#5F6B62] font-body">
                Free 15-minute consultation — see if we're a fit before
                committing.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO BAND */}
      <Section testId="intro-section" className="!py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>The work, in plain language</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-[1.05]">
              As Oliver Goldsmith said, <em className="italic text-[#5F6B62]">“life is a journey to be experienced, not a problem to be solved.”</em> Trauma is not a verdict. It's a wound that can heal. Let's embark on this healing journey together.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 font-body text-lg text-[#5F6B62] leading-relaxed">
            <p>
              I'm Jeremy — a Licensed Clinical Social Worker practicing in
              Denver. My work centers on what happens after life throws us
              curve balls: the way the body holds it, the way the mind tries
              to make sense of it, the way ordinary days quietly carry it.
            </p>
            <p>
              Maybe you're feeling burnt out, overwhelmed, anxious, or
              depressed navigating life's curveballs even if you look
              put-together on the outside. Maybe you're going through a
              difficult transition, have endured trauma, are navigating
              grief, or are just ready to become the best version of
              yourself. I strive to provide you with the tools and
              strategies necessary to meet life's challenges head on in a
              way that works for you.
            </p>
            <p>
              I utilize EMDR, Trauma-Focused CBT, WET, CBT, and DBT — not
              because the acronyms matter, but because different wounds need
              different kinds of care. The pace is yours. The relationship
              comes first.
            </p>
          </div>
        </div>
      </Section>

      {/* SPECIALTIES — bento */}
      <Section testId="specialties-section" className="!pt-4">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Approaches</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-4 text-[#2E3630]">How we might work together.</h2>
          </div>
          <Link
            to="/services"
            className="font-body text-sm text-[#6B8E7B] hover:text-[#557564] flex items-center gap-2"
            data-testid="specialties-see-all"
          >
            All services <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {SPECIALTIES.map(({ icon: Icon, label, title, body }) => (
            <article
              key={label}
              data-testid={`specialty-card-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className="group bg-white border border-[#E8E5DF] rounded-[2rem] p-8 sm:p-10 transition-transform duration-500 hover:-translate-y-1"
            >
              <div>
                <div className="flex items-center justify-between">
                  <Eyebrow>{label}</Eyebrow>
                  <span className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B] group-hover:bg-[#6B8E7B] group-hover:text-white transition-colors">
                    <Icon size={18} />
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-6 text-[#2E3630] leading-tight">{title}</h3>
                <p className="font-body mt-5 text-[#5F6B62] leading-relaxed">{body}</p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* INCLUSIVITY BAND */}
      <section className="relative py-24 sm:py-32" data-testid="inclusive-section">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #F0EDE6 0%, #F9F7F4 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_mindcare-clinic-8/artifacts/j41ys97r_military%20photo.webp"
              alt="Service members boarding a military aircraft"
              className="rounded-[2rem] w-full h-[420px] object-cover"
            />
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>A room without conditions</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-[1.05]">
              For those who've served and their families.
            </h2>
            <p className="mt-8 font-body text-[#5F6B62] leading-relaxed max-w-2xl">
              Veterans don't need to explain the silence after coming home and
              military families don't need to explain the hardships of having a
              loved one on active deployment. I work with military veterans and
              their families with transitions, post-deployment trauma, moral
              injury, and the quieter kinds of grief that can take years to
              name.
            </p>

            <h3 className="font-serif-display text-3xl sm:text-5xl mt-12 text-[#2E3630] leading-[1.05]">
              For those who've had to translate.
            </h3>
            <p className="mt-5 font-body text-[#5F6B62] leading-relaxed max-w-2xl">
              LGBTQ+ clients shouldn't have to teach their therapist. Your
              pronouns are used. Your identity isn't a topic unless you make
              it one. Affirming care is the floor, not the ceiling. I work
              with LGBTQ+ clients who are trying to navigate life and society
              and their families who want to support their loved ones in the
              best ways possible.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <Section testId="home-cta-section" className="!pb-32">
        <div className="rounded-[2rem] border border-[#E8E5DF] bg-white p-10 sm:p-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Eyebrow>Next step</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-5 text-[#2E3630] leading-tight">
              You don't have to know what's wrong to begin.
            </h2>
            <p className="font-body mt-5 text-lg text-[#5F6B62] max-w-xl">
              A 15-minute consultation is free, low-pressure, and a good way
              to hear my voice before deciding anything.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-cta-book"
              className="btn-pill btn-primary"
            >
              Request a consultation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
